import { Link } from 'react-router-dom';
import { formatMoney } from '@lombok/shared';
import { Card, CardContent, CardHeader, CardTitle } from '@lombok/ui';
import { PageHeader } from '@/components/PageHeader';
import { useCustomers, useHealth, useProducts, useReportsDashboard } from '@/data/hooks';

/**
 * Admin landing dashboard. Live operational snapshot: customer/product counts, AR open
 * balance + pending-payments from the reports endpoint, open-orders-by-status, and an API
 * health indicator. Desktop-dense per the docs/11 admin direction.
 */
export function AdminDashboard() {
  const health = useHealth();
  const customers = useCustomers({ limit: 100 });
  const products = useProducts({ limit: 100 });
  const report = useReportsDashboard();
  const r = report.data;

  const customerCount = customers.data?.items.length;
  const productCount = products.data?.items.length;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Panel"
        description="Resumen operativo de Lombok Commerce OS."
        actions={<ApiHealthPill state={healthState(health)} />}
      />

      {/* KPI grid */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          label="Clientes"
          value={fmtCount(customerCount, customers.isLoading)}
          to="/app/admin/clientes"
        />
        <KpiCard
          label="Productos"
          value={fmtCount(productCount, products.isLoading)}
          to="/app/admin/productos"
        />
        <KpiCard
          label="Saldo pendiente"
          value={fmtReportMoney(report, r?.ar.totalOpenMinorUsd)}
          hint={r ? `${r.ar.openInvoiceCount} factura(s) abiertas` : undefined}
          to="/app/admin/facturas"
        />
        <KpiCard
          label="Pagos por revisar"
          value={fmtReportCount(report, r?.payments.pendingCount)}
          hint={r ? `${formatMoney(r.payments.pendingMinorUsd)} por aprobar` : undefined}
          to="/app/admin/pagos"
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        {/* Quick actions */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Acciones rápidas</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3 sm:grid-cols-2">
            <QuickAction
              to="/app/admin/clientes"
              title="Clientes"
              desc="Buscar, ver crédito y estado de cuenta"
            />
            <QuickAction
              to="/app/admin/productos"
              title="Productos"
              desc="Catálogo y disponibilidad"
            />
            <QuickAction
              to="/app/admin/pedidos"
              title="Pedidos"
              desc="Revisar y aprobar pedidos"
            />
            <QuickAction
              to="/app/admin/pagos"
              title="Pagos"
              desc="Aprobar comprobantes de pago"
            />
          </CardContent>
        </Card>

        {/* System status */}
        <Card>
          <CardHeader>
            <CardTitle>Estado del sistema</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-3 text-sm">
            <StatusRow label="API" state={healthState(health)} />
            <p className="text-xs text-text-muted">
              Los datos se obtienen del backend mediante el cliente tipado. Si la API no está
              disponible localmente, las listas mostrarán un estado de error.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Live operational snapshot: open orders by status. */}
      <Card>
        <CardHeader>
          <CardTitle>Pedidos por estado</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 gap-3 pt-0 sm:grid-cols-3 lg:grid-cols-6">
          {ORDER_STATUS_ROWS.map((s) => (
            <div key={s.key} className="rounded-md border border-border p-3">
              <p className="text-2xl font-semibold tabular-nums text-text-primary">
                {fmtReportCount(report, r?.orders.byStatus[s.key])}
              </p>
              <p className="mt-0.5 text-xs text-text-muted">{s.label}</p>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

/** The dashboard's open-orders status set (matches DashboardReport.orders.byStatus keys). */
const ORDER_STATUS_ROWS: { key: string; label: string }[] = [
  { key: 'submitted', label: 'Enviados' },
  { key: 'pending_approval', label: 'Por aprobar' },
  { key: 'approved', label: 'Aprobados' },
  { key: 'dispatched', label: 'Despachados' },
  { key: 'delivered', label: 'Entregados' },
  { key: 'cancelled', label: 'Cancelados' },
];

type HealthState = 'loading' | 'ok' | 'down';

function healthState(q: ReturnType<typeof useHealth>): HealthState {
  if (q.isLoading) return 'loading';
  if (q.isError || q.data?.status !== 'ok') return 'down';
  return 'ok';
}

function fmtCount(n: number | undefined, loading: boolean): string {
  if (loading) return '…';
  if (n === undefined) return '—';
  return String(n);
}

/** Format a count from the reports query: loading → "…", missing/error → "—". */
function fmtReportCount(q: ReturnType<typeof useReportsDashboard>, n: number | undefined): string {
  if (q.isLoading) return '…';
  if (q.isError || n === undefined) return '—';
  return String(n);
}

/** Format a money value (minor USD) from the reports query, with loading/error fallbacks. */
function fmtReportMoney(q: ReturnType<typeof useReportsDashboard>, n: number | undefined): string {
  if (q.isLoading) return '…';
  if (q.isError || n === undefined) return '—';
  return formatMoney(n);
}

function KpiCard({
  label,
  value,
  hint,
  to,
}: {
  label: string;
  value: string;
  hint?: string;
  to?: string;
}) {
  const body = (
    <Card interactive={!!to} className="h-full">
      <CardContent className="pt-5">
        <p className="text-sm text-text-muted">{label}</p>
        <p className="mt-1 text-2xl font-semibold tracking-tight text-text-primary">{value}</p>
        {hint ? <p className="mt-1 text-xs text-text-muted">{hint}</p> : null}
      </CardContent>
    </Card>
  );
  return to ? (
    <Link to={to} className="rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-surface-muted">
      {body}
    </Link>
  ) : (
    body
  );
}

function QuickAction({ to, title, desc }: { to: string; title: string; desc: string }) {
  return (
    <Link
      to={to}
      className="flex flex-col gap-1 rounded-md border border-border p-4 transition-[background-color,border-color,transform] duration-150 ease-out hover:border-border-strong hover:bg-surface-muted active:scale-[0.99] motion-reduce:active:scale-100"
    >
      <span className="text-sm font-semibold text-text-primary">{title}</span>
      <span className="text-xs text-text-muted">{desc}</span>
    </Link>
  );
}

function ApiHealthPill({ state }: { state: HealthState }) {
  const map: Record<HealthState, { label: string; dot: string; text: string }> = {
    loading: { label: 'Verificando API', dot: 'bg-warning', text: 'text-text-muted' },
    ok: { label: 'API operativa', dot: 'bg-success', text: 'text-text-primary' },
    down: { label: 'API no disponible', dot: 'bg-danger', text: 'text-danger' },
  };
  const m = map[state];
  return (
    <span className={`inline-flex items-center gap-2 text-sm ${m.text}`}>
      <span className={`h-2.5 w-2.5 rounded-full ${m.dot}`} />
      {m.label}
    </span>
  );
}

function StatusRow({ label, state }: { label: string; state: HealthState }) {
  const map: Record<HealthState, { label: string; dot: string }> = {
    loading: { label: 'Verificando…', dot: 'bg-warning' },
    ok: { label: 'Operativa', dot: 'bg-success' },
    down: { label: 'No disponible', dot: 'bg-danger' },
  };
  const m = map[state];
  return (
    <div className="flex items-center justify-between">
      <span className="text-text-muted">{label}</span>
      <span className="inline-flex items-center gap-2 font-medium text-text-primary">
        <span className={`h-2 w-2 rounded-full ${m.dot}`} />
        {m.label}
      </span>
    </div>
  );
}
